import { Snowflake, Sparkles, Coffee, Bath, Shirt, Wifi, Refrigerator, Layers, Speaker, Ruler, ZoomIn } from "lucide-react";

import { useEffect, useState } from "react";
import { Lightbox } from "@/components/site/Lightbox";
import { useSiteImages } from "@/hooks/useSiteImages";

const imgSrc = (u) => (u && u.startsWith("/") ? `${process.env.REACT_APP_BACKEND_URL}${u}` : u);

const amenities = [
  { icon: Ruler, label: "550 sqft studio space" },
  { icon: Snowflake, label: "Air-conditioned studio" },
  { icon: Sparkles, label: "Separate makeup & changing room" },
  { icon: Coffee, label: "Pantry area" },
  { icon: Bath, label: "Washroom" },
  { icon: Shirt, label: "Clothing rack" },
  { icon: Wifi, label: "High-speed Wi-Fi" },
  { icon: Refrigerator, label: "Mini refrigerator" },
  { icon: Speaker, label: "Bluetooth speaker" },
  { icon: Layers, label: "Carpets & styling elements" },
];

const backdrops = [
  { name: "European-style wall", type: "Permanent set", cat: "european-style-wall" },
  { name: "Painted arch backdrop", type: "Movable", cat: "painted-arch-backdrop" },
  { name: "Printed wall backdrop", type: "Movable", cat: "printed-wall-backdrop" },
  { name: "Paper backdrops", type: "Movable", cat: "paper-backdrops" },
];

export const StudioDetails = () => {
  const [categories, setCategories] = useState({});
  const [lightbox, setLightbox] = useState({ photos: [], index: null });

  const data = useSiteImages();

  useEffect(() => {
    if (data) setCategories(data.categories || {});
  }, [data]);

  return (
  <>
    {/* The Studio + Amenities */}
    <section id="amenities" data-testid="amenities-section" className="py-20 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="max-w-3xl mb-14">
          <span className="label-caps text-forest block mb-2">The Studio</span>
          <h2 data-testid="amenities-heading" className="heavy-heading text-3xl sm:text-4xl font-extrabold text-ink mb-4">
            Comfortable, from start to finish.
          </h2>
          <p className="text-base text-muted-gd leading-relaxed">
            A versatile, air-conditioned studio space designed to make your shoot comfortable from
            start to finish.
          </p>
        </div>
        <p className="label-caps text-muted-g mb-6">Studio Amenities</p>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {amenities.map((a) => (
            <div
              key={a.label}
              data-testid={`amenity-${a.label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
              className="bg-bg rounded-xl border border-line p-5 flex flex-col gap-3"
            >
              <a.icon size={22} strokeWidth={1.75} className="text-forest" />
              <p className="text-sm font-semibold text-ink leading-snug">{a.label}</p>
            </div>
          ))}
        </div>
      </div>
    </section>

    {/* Backdrops */}
    <section id="backdrops" data-testid="backdrops-section" className="py-20 bg-bg border-y border-line">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="max-w-3xl mb-14">
          <span className="label-caps text-forest block mb-2">Backdrops</span>
          <h2 data-testid="backdrops-heading" className="heavy-heading text-3xl sm:text-4xl font-extrabold text-ink mb-4">
            A different setting for every idea.
          </h2>
          <p className="text-base text-muted-gd leading-relaxed">
            Choose from a selection of permanent and movable setups to create different moods
            within the same space.
          </p>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {backdrops.map((b) => {
            const photos = b.cat ? categories[b.cat] || [] : [];
            return (
              <article
                key={b.name}
                data-testid={`backdrop-${b.name.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                className="bg-white rounded-xl border border-line-2 overflow-hidden"
              >
                {photos.length > 0 && (
                  <button
                    type="button"
                    onClick={() => setLightbox({ photos, index: 0 })}
                    data-testid={`backdrop-photos-${b.cat}`}
                    aria-label={`View ${b.name} photos`}
                    className="relative block w-full aspect-[4/3] group cursor-zoom-in"
                  >
                    <img
                      src={imgSrc(photos[0])}
                      alt={b.name}
                      loading="lazy"
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                    />
                    <span className="absolute inset-0 bg-ink/0 group-hover:bg-ink/30 transition-colors duration-300 flex items-center justify-center">
                      <ZoomIn size={22} className="text-white opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                    </span>
                    {photos.length > 1 && (
                      <span className="absolute bottom-2 right-2 bg-ink/70 text-white text-[10px] font-mono px-2 py-0.5 rounded">
                        {photos.length} photos
                      </span>
                    )}
                  </button>
                )}
                <div className="p-6">
                  <span
                    className={`inline-block text-[10px] font-bold uppercase tracking-wider px-2.5 py-1 rounded mb-4 ${
                      b.type === "Permanent set" ? "bg-forest text-white" : "bg-surface-2 text-forest border border-line-2"
                    }`}
                  >
                    {b.type}
                  </span>
                  <h3 className="text-lg font-bold text-ink leading-snug">{b.name}</h3>
                </div>
              </article>
            );
          })}
        </div>
      </div>
    </section>

    <Lightbox
      photos={lightbox.photos}
      index={lightbox.index}
      onClose={() => setLightbox({ photos: [], index: null })}
      onStep={(d) =>
        setLightbox((s) => ({
          ...s,
          index: (s.index + d + s.photos.length) % s.photos.length,
        }))
      }
    />
  </>
  );
};
